import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { DropinTicketPublic, VATRate, Client, Currency } from '../../../pure-js/types/GrooverTypes'
import { useDropinTicketPublic, getClient } from '../libs/DBApiHandler'
import { getVatFromBruttoPrice } from '../../../pure-js/libs/EnrollmentHelper'
import { DEFAULT_CURRENCY, getCurrencyForClient } from '../../../pure-js/libs/currencyHelper'
import Box from '../components/Box'
import GrooverText from '../components/GrooverText'
import { ReceiptSsnInputForm } from '../components/ReceiptSsnInputForm'
import { addSsnForDropinTicket } from '../libs/CloudFunctionsApiHandler'
import { useReceiptSsn } from '../hooks/useReceiptSsn'
import { downloadReceiptPdf } from '../libs/downloadReceiptPdf'
import { getDropinReceiptSchoolName } from '../libs/getDropinReceiptSchoolName'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { getPaymentMethodLabel } from '../utils/paymentMethodDisplayHelper'
import penIcon from '/public/penIcon.svg'
import plusIcon from '/public/plusIcon.svg'
import printIcon from '/public/printIcon.svg'

const RECEIPT_ELEMENT_ID = 'dropin-ticket-receipt'

const formatDate = (dateString: string): string => new Date(dateString).toLocaleDateString('sv-SE')

const DropinTicketDetailPage: React.FC = () => {
  const { dropinTicketId } = useParams<{ dropinTicketId: string }>()
  const { data: ticket, isLoading, error } = useDropinTicketPublic(dropinTicketId || null)
  const [client, setClient] = useState<Client | null>(null)
  const [currency, setCurrency] = useState<Currency>(DEFAULT_CURRENCY as Currency)

  const { showSsnInput, setShowSsnInput, savedSsn, isSavingSsn, ssnFormProps, handleSaveSsn } = useReceiptSsn(
    (ssn) => addSsnForDropinTicket({ clientId: ticket!.clientId, dropinTicketId: dropinTicketId!, ssn }),
    currency
  )

  const schoolDisplayName = getDropinReceiptSchoolName(client?.name, ticket?.clientName, ticket?.schoolName)

  const handleDownloadPDF = useCallback(
    () => downloadReceiptPdf(RECEIPT_ELEMENT_ID, `${schoolDisplayName ?? 'Dropin'}_Receipt.pdf`),
    [schoolDisplayName]
  )

  // Live client: currency, org/address, and current school name for the receipt header
  useEffect(() => {
    const fetchClient = async () => {
      if (ticket?.clientId) {
        const clientData = await getClient(ticket.clientId)
        if (clientData) {
          setClient(clientData)
          setCurrency(getCurrencyForClient(clientData))
        }
      }
    }
    fetchClient()
  }, [ticket?.clientId])

  // Calculate VAT
  const vatCalculations = useMemo(() => {
    if (!ticket) return null

    const priceInCents = ticket.price // Price is in cents (gross)
    const priceInSEK = priceInCents / 100 // Convert from cents to SEK for display
    const vatRate = ticket.vat as VATRate
    // getVatFromBruttoPrice expects price in SEK and returns VAT in SEK
    const vatAmountInSEK = getVatFromBruttoPrice(priceInSEK, vatRate)
    const netAmountInSEK = priceInSEK - vatAmountInSEK

    return {
      grossPrice: priceInSEK,
      vatRate,
      vatAmount: vatAmountInSEK,
      netAmount: netAmountInSEK
    }
  }, [ticket])

  if (isLoading) {
    return (
      <Container>
        <Box fullWidth align="center" justify="center" style={{ padding: '40px' }}>
          <LoadingText>Loading...</LoadingText>
        </Box>
      </Container>
    )
  }

  if (error || !ticket) {
    console.error('Error fetching dropin ticket:', dropinTicketId, error)
    return (
      <Container>
        <Box fullWidth align="center" justify="center" style={{ padding: '40px' }}>
          <ErrorText>Ticket not found</ErrorText>
        </Box>
      </Container>
    )
  }

  if (!vatCalculations) {
    return null
  }

  return (
    <Container>
      <ContentBox direction="column" gap="24px" fullWidth>
        {/* Receipt actions row */}
        <ReceiptActionsRow direction="row" align="center" justify="space-between" fullWidth>
          <ReceiptSectionTitle>
            <GrooverText textKey="registrationStep5PaymentReceiptHeader" />
          </ReceiptSectionTitle>
          <ActionButtons align="center" gap="16px">
            <SsnToggle type="button" onClick={() => setShowSsnInput(!showSsnInput)}>
              <img src={savedSsn ? penIcon : plusIcon} width={16} height={16} />
              <GrooverText
                textKey={savedSsn ? 'registrationCtactaEditPersonalNumber' : 'registrationCtactaAddPersonalNumber'}
              />
            </SsnToggle>
            <DownloadButton type="button" onClick={handleDownloadPDF}>
              <img src={printIcon} width={16} height={16} />
              <GrooverText textKey="registrationCtactaDownloadReceipt" />
            </DownloadButton>
          </ActionButtons>
        </ReceiptActionsRow>

        {/* SSN input (shown when toggle is open) */}
        {showSsnInput && (
          <ReceiptSsnInputForm
            control={ssnFormProps.control}
            handleSubmit={ssnFormProps.handleSubmit}
            formState={ssnFormProps.formState}
            isSaving={isSavingSsn}
            onSave={handleSaveSsn}
          />
        )}

        {/* Receipt Section */}
        <ReceiptCard id={RECEIPT_ELEMENT_ID}>
          <ReceiptHeader>
            <ClientName>{schoolDisplayName}</ClientName>
            <ReceiptTitle>
              <GrooverText textKey="registrationStep5PaymentReceiptHeader" />
            </ReceiptTitle>
          </ReceiptHeader>

          {client && (client.orgNo || client.address) && (
            <SellerMeta>
              {client.orgNo && <SellerMetaLine>Org.nr: {client.orgNo}</SellerMetaLine>}
              {client.address && <SellerMetaLine>{client.address}</SellerMetaLine>}
            </SellerMeta>
          )}

          <ReceiptGrid>
            <Label>
              <GrooverText textKey="componentsReceiptReceiptPaymentDateLabel" />
            </Label>
            <Value>{formatDate(ticket.createdAt)}</Value>

            <Label>Receipt ID</Label>
            <Value>{ticket.id}</Value>

            <Label>
              <GrooverText textKey="portalReceiptPackage" />
            </Label>
            <Value>{ticket.packageName || `${ticket.ticketCount} tickets`}</Value>

            {savedSsn && (
              <>
                <Label>
                  <GrooverText textKey="receiptPersonalNumberLabel" />
                </Label>
                <Value>{savedSsn}</Value>
              </>
            )}

            <Label>Price (gross)</Label>
            <Value>
              {vatCalculations.grossPrice.toFixed(2)} {currency}
            </Value>

            <Label>VAT ({vatCalculations.vatRate}%)</Label>
            <Value>
              {vatCalculations.vatAmount.toFixed(2)} {currency}
            </Value>

            <Label>Net amount</Label>
            <Value>
              {vatCalculations.netAmount.toFixed(2)} {currency}
            </Value>

            <Label>{getGrooverText('portalReceiptPaymentMethod')}</Label>
            <Value>{getPaymentMethodLabel(ticket.paymentMethod)}</Value>
          </ReceiptGrid>

          <Divider />

          <TotalRow direction="row" justify="space-between" align="center" fullWidth>
            <TotalLabel>Total</TotalLabel>
            <TotalValue>
              {vatCalculations.grossPrice.toFixed(2)} {currency}
            </TotalValue>
          </TotalRow>
        </ReceiptCard>
      </ContentBox>
    </Container>
  )
}

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
`

const ContentBox = styled(Box)`
  max-width: 600px;
  margin: 0 auto;
`

const ReceiptCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e6e6e6;
`

const ReceiptHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`

const ClientName = styled.div`
  font-weight: 700;
  color: #000;
  font-size: 18px;
`

const ReceiptTitle = styled.div`
  color: #000;
  font-size: 16px;
`

const ReceiptGrid = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 12px 24px;
  align-items: baseline;
`

const Label = styled.div`
  color: #666;
  font-size: 14px;
  white-space: nowrap;

  & * {
    color: inherit;
    font-size: inherit;
    font-weight: inherit;
  }
`

const Value = styled.div`
  color: #000;
  word-break: break-word;
  font-size: 14px;
`

const Divider = styled.div`
  height: 1px;
  background: #e6e6e6;
  margin: 20px 0;
`

const TotalRow = styled(Box)``

const TotalLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`

const TotalValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: var(--portal-primary, #7c3aed);
`

const LoadingText = styled.div`
  color: #666;
  font-size: 16px;
`

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 16px;
`

const ReceiptActionsRow = styled(Box)`
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const ReceiptSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #000;
  margin: 0;
`

const ActionButtons = styled(Box)`
  flex-direction: row;

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;
  }
`

const SsnToggle = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--portal-primary, #7c3aed);
  font-size: 14px;
  font-weight: 500;

  & * {
    color: inherit;
  }

  &:hover {
    opacity: 0.8;
  }
`

const DownloadButton = styled.button`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--portal-primary, #7c3aed);
  font-size: 14px;
  font-weight: 500;

  & * {
    color: inherit;
  }

  &:hover {
    opacity: 0.8;
  }
`

const SellerMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid #e6e6e6;
`

const SellerMetaLine = styled.div`
  font-size: 12px;
  color: #6b7280;
`

export default DropinTicketDetailPage
